/**
 * Application Constants
 *
 * Centralized constants used throughout the application
 *
 * @module utils/constants
 */

/**
 * Project statuses
 *
 * Matches the DB CHECK constraint added by
 * `supabase/migrations/20260705035001_fix_project_status_enum_and_transitions.sql`
 * (the set migration 014 intended but never actually installed -- see issue #296).
 * The pre-014 values ('draft', 'quoted', 'active') are NOT valid anymore; every
 * project now starts at QUOTATION_SENT. Do not reintroduce the legacy values here --
 * that's the exact bug class issue #296 fixed (stale enum after a DB rename).
 */
export const PROJECT_STATUS = {
  QUOTATION_SENT: 'quotation_sent',
  QUOTATION_ACCEPTED: 'quotation_accepted',
  PO_RECEIVED: 'po_received',
  IN_PROGRESS: 'in_progress',
  CLOSED: 'closed',
};

/**
 * Reserved project_number prefix for per-user "personal tasks" projects
 * (issue #405). The MCP agent creates one of these implicit containers when a
 * user asks for a task with no project (Jorge's to-do list). They are real rows
 * but must never appear in normal project lists / dashboards / reports — filter
 * them out by this prefix. Must stay in sync with the MCP-server constant
 * `PERSONAL_PROJECT_NUMBER_PREFIX` in mcp-server/src/constants/personalProject.js.
 */
export const PERSONAL_PROJECT_NUMBER_PREFIX = 'PERSONAL';

/**
 * Applies the "hide personal-tasks containers" filter to a Supabase `projects`
 * query builder. Single source of truth so every list view filters identically.
 * @param {object} query - A supabase-js query builder over `projects`.
 * @returns {object} The same builder with the exclusion applied (chainable).
 */
export const excludePersonalProjects = (query) =>
  query.not('project_number', 'ilike', `${PERSONAL_PROJECT_NUMBER_PREFIX}-%`);

/**
 * ¿Este `project_number` es el de un contenedor personal (#405/#791)? Único
 * lugar que sabe el formato (`PERSONAL-{userId}`) — así lo que pinta "Mi lista
 * personal" en vez del título crudo ("Tareas personales — …") y lo que decide
 * si mostrarlo en un selector de proyectos leen la MISMA regla.
 * @param {string|null|undefined} projectNumber
 * @returns {boolean}
 */
export const esProyectoPersonal = (projectNumber) =>
  typeof projectNumber === 'string'
  && projectNumber.toUpperCase().startsWith(`${PERSONAL_PROJECT_NUMBER_PREFIX}-`);

/**
 * Project statuses considered "budget-tracked" — i.e. eligible to appear in
 * budget alert/usage widgets (dashboard counter, Resumen de Presupuestos table).
 *
 * Single source of truth so the budget alert counter and the budget table
 * always agree on which projects are in scope (see issue #261: the counter
 * used to only look at ACTIVE + PO_RECEIVED while the table looked at
 * ACTIVE + QUOTED, so a QUOTED project already over budget would show up
 * in the table with an ALERTA badge but not be counted at all).
 *
 * Post-#296 equivalents: the old QUOTED/ACTIVE values collapsed into
 * QUOTATION_ACCEPTED/IN_PROGRESS in the post-014 status set (QUOTATION_SENT,
 * the very first status now, is excluded here just like the pre-quotation
 * DRAFT phase was excluded before -- budget tracking starts once the project
 * has moved past the initial quotation-sent stage).
 */
export const BUDGET_TRACKED_PROJECT_STATUSES = [
  PROJECT_STATUS.QUOTATION_ACCEPTED,
  PROJECT_STATUS.PO_RECEIVED,
  PROJECT_STATUS.IN_PROGRESS,
];

/**
 * Project statuses a project can be in and still be invoiced.
 *
 * This is the FULL set accepted by the DB CHECK constraint
 * `projects_status_check` (verified against prod):
 *   draft | quotation_sent | quotation_accepted | po_received | in_progress | closed
 *
 * There is no "cancelled" status, so no project is excluded: a closed project is
 * in fact the *most* common thing to invoice (you bill after the work is done).
 *
 * Why this constant exists: `components/invoices/InvoiceForm.js` used to filter
 * the project dropdown with `['active', 'quoted', 'draft']`. Two of those three
 * values were killed by #296 and never existed in the CHECK, and nothing in prod
 * sits in `draft`, so the query matched 0 of 73 projects. The selector rendered
 * empty and "Crear Factura" stayed disabled forever — no invoice could be created
 * at all, and the UI never said why (an empty `<select>` reads as "no hay
 * proyectos", not as "this filter is wrong"). Same bug class as #296: a hardcoded
 * status list drifting away from the DB after a rename.
 *
 * `draft` is intentionally spelled as a literal instead of via PROJECT_STATUS:
 * it is valid in the DB CHECK but is NOT part of the application-level status
 * machine above, and adding it to PROJECT_STATUS would put it in every status
 * dropdown as a selectable state. Here we only need to *accept* rows that carry
 * it, not offer it.
 */
export const INVOICEABLE_PROJECT_STATUSES = [
  'draft',
  PROJECT_STATUS.QUOTATION_SENT,
  PROJECT_STATUS.QUOTATION_ACCEPTED,
  PROJECT_STATUS.PO_RECEIVED,
  PROJECT_STATUS.IN_PROGRESS,
  PROJECT_STATUS.CLOSED,
];

/**
 * Quotation statuses
 */
export const QUOTATION_STATUS = {
  DRAFT: 'draft',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  SENT: 'sent',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

/**
 * Invoice statuses (matches CHECK constraint in migration 033)
 */
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  SENT: 'sent',
  PARTIAL_PAYMENT: 'partial_payment',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled',
};

export const INVOICE_STATUS_LABELS = {
  draft: 'Borrador',
  pending: 'Pendiente',
  sent: 'Enviada',
  partial_payment: 'Pago Parcial',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
};

export const INVOICE_STATUS_VARIANTS = {
  draft: 'secondary',
  pending: 'info',
  sent: 'primary',
  partial_payment: 'warning',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'dark',
};

export const INVOICE_PAYMENT_METHOD = {
  TRANSFER: 'transfer',
  CHECK: 'check',
  CASH: 'cash',
  CREDIT_CARD: 'credit_card',
  OTHER: 'other',
};

export const INVOICE_PAYMENT_METHOD_LABELS = {
  transfer: 'Transferencia',
  check: 'Cheque',
  cash: 'Efectivo',
  credit_card: 'Tarjeta de Crédito',
  other: 'Otro',
};

/**
 * Expense categories
 */
export const EXPENSE_CATEGORY = {
  MATERIALS: 'materials',
  TOOLS: 'tools',
  LABOR: 'labor',
  TRANSPORTATION: 'transportation',
  OTHER: 'other',
};

/**
 * Item types for quotations
 */
export const ITEM_TYPE = {
  MATERIAL: 'material',
  TOOL: 'tool',
  LABOR: 'labor',
  ADMIN: 'admin',
  OTHER: 'other',
};

/**
 * Stock movement types
 */
export const MOVEMENT_TYPE = {
  ENTRY: 'entry',
  EXIT: 'exit',
  ADJUSTMENT: 'adjustment',
};

/**
 * Inventory categories
 */
export const INVENTORY_CATEGORY = {
  MATERIALS: 'materials',
  TOOLS: 'tools',
  CONSUMABLES: 'consumables',
  SPARE_PARTS: 'spare_parts',
  ELECTRICAL: 'electrical',
  MECHANICAL: 'mechanical',
  CHEMICALS: 'chemicals',
  SAFETY: 'safety',
  OTHER: 'other',
};

/**
 * Inventory category labels
 */
export const INVENTORY_CATEGORY_LABELS = {
  [INVENTORY_CATEGORY.MATERIALS]: 'Materiales',
  [INVENTORY_CATEGORY.TOOLS]: 'Herramientas',
  [INVENTORY_CATEGORY.CONSUMABLES]: 'Consumibles',
  [INVENTORY_CATEGORY.SPARE_PARTS]: 'Repuestos',
  [INVENTORY_CATEGORY.ELECTRICAL]: 'Eléctrico',
  [INVENTORY_CATEGORY.MECHANICAL]: 'Mecánico',
  [INVENTORY_CATEGORY.CHEMICALS]: 'Químicos',
  [INVENTORY_CATEGORY.SAFETY]: 'Seguridad',
  [INVENTORY_CATEGORY.OTHER]: 'Otros',
};

/**
 * Movement reason types
 */
export const MOVEMENT_REASON = {
  PROJECT_CONSUMPTION: 'project_consumption',
  LOSS_DAMAGE: 'loss_damage',
  SUPPLIER_RETURN: 'supplier_return',
  TRANSFER: 'transfer',
  INVENTORY_ADJUSTMENT: 'inventory_adjustment',
  INITIAL_STOCK: 'initial_stock',
  PURCHASE: 'purchase',
};

/**
 * Movement reason labels
 */
export const MOVEMENT_REASON_LABELS = {
  [MOVEMENT_REASON.PROJECT_CONSUMPTION]: 'Consumo en proyecto',
  [MOVEMENT_REASON.LOSS_DAMAGE]: 'Merma / Pérdida',
  [MOVEMENT_REASON.SUPPLIER_RETURN]: 'Devolución a proveedor',
  [MOVEMENT_REASON.TRANSFER]: 'Transferencia',
  [MOVEMENT_REASON.INVENTORY_ADJUSTMENT]: 'Ajuste de inventario',
  [MOVEMENT_REASON.INITIAL_STOCK]: 'Stock inicial',
  [MOVEMENT_REASON.PURCHASE]: 'Compra',
};

/**
 * User roles
 */
export const USER_ROLE = {
  ADMIN: 'admin',
  TECHNICIAN: 'technician',
  EMPLOYEE: 'technician', // backward compat alias
  CLIENT: 'client',
};

/**
 * Task statuses
 */
export const TASK_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  REVIEW: 'review',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/**
 * Task priorities
 */
export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

/**
 * Task categories
 */
export const TASK_CATEGORY = {
  INSTALLATION: 'installation',
  MAINTENANCE: 'maintenance',
  REPAIR: 'repair',
  INSPECTION: 'inspection',
  DOCUMENTATION: 'documentation',
  GENERAL: 'general',
  QUOTATION_REVIEW: 'quotation_review',
  OTHER: 'other',
};

/**
 * Task category labels (Spanish)
 */
export const TASK_CATEGORY_LABELS = {
  [TASK_CATEGORY.INSTALLATION]: 'Instalación',
  [TASK_CATEGORY.MAINTENANCE]: 'Mantenimiento',
  [TASK_CATEGORY.REPAIR]: 'Reparación',
  [TASK_CATEGORY.INSPECTION]: 'Inspección',
  [TASK_CATEGORY.DOCUMENTATION]: 'Documentación',
  [TASK_CATEGORY.GENERAL]: 'General',
  [TASK_CATEGORY.OTHER]: 'Otros',
};

/**
 * Task assignment roles
 */
export const TASK_ASSIGNMENT_ROLE = {
  ASSIGNEE: 'assignee',
  REVIEWER: 'reviewer',
  OBSERVER: 'observer',
};

/**
 * Task material statuses
 */
export const TASK_MATERIAL_STATUS = {
  PLANNED: 'planned',
  RESERVED: 'reserved',
  CONSUMED: 'consumed',
  RETURNED: 'returned',
};

/**
 * Budget alert thresholds
 */
export const BUDGET_THRESHOLD = {
  WARNING: 80, // Yellow alert at 80%
  CRITICAL: 100, // Red alert at 100%
};

/**
 * File upload constraints
 */
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ACCEPTED_TYPES: {
    IMAGES: ['image/jpeg', 'image/jpg', 'image/png'],
    DOCUMENTS: ['application/pdf'],
    RECEIPTS: ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
    // #451: what the chat composer accepts. `CHAT_IMAGES` is wider than
    // `IMAGES` because the `chat-attachments` bucket has always allowed
    // webp/gif; `CHAT_DOCUMENTS` is the new part (PDF, Word, Excel,
    // PowerPoint, CSV, plain text, zip).
    //
    // IMPORTANT: this list MUST stay in sync with the bucket's
    // `allowed_mime_types` in `supabase/migrations/*_chat_attachments_documents.sql`.
    // Storage rejects the upload by MIME type before any RLS policy runs, so
    // widening the <input accept> alone does nothing.
    CHAT_IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
    CHAT_DOCUMENTS: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/csv',
      'text/plain',
      'application/zip',
    ],
  },
};

/**
 * Every MIME type the chat composer accepts (images + documents).
 * Derived so there is a single place to add a type (#451).
 */
export const CHAT_ACCEPTED_TYPES = [
  ...FILE_UPLOAD.ACCEPTED_TYPES.CHAT_IMAGES,
  ...FILE_UPLOAD.ACCEPTED_TYPES.CHAT_DOCUMENTS,
];

/**
 * Human labels for the file-type error message and for the attachment chip
 * icon, keyed by MIME type (#451).
 */
export const CHAT_FILE_LABELS = {
  'application/pdf': { label: 'PDF', icon: 'bi-file-earmark-pdf' },
  'application/msword': { label: 'Word', icon: 'bi-file-earmark-word' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { label: 'Word', icon: 'bi-file-earmark-word' },
  'application/vnd.ms-excel': { label: 'Excel', icon: 'bi-file-earmark-excel' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { label: 'Excel', icon: 'bi-file-earmark-excel' },
  'application/vnd.ms-powerpoint': { label: 'PowerPoint', icon: 'bi-file-earmark-slides' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { label: 'PowerPoint', icon: 'bi-file-earmark-slides' },
  'text/csv': { label: 'CSV', icon: 'bi-file-earmark-spreadsheet' },
  'text/plain': { label: 'Texto', icon: 'bi-file-earmark-text' },
  'application/zip': { label: 'ZIP', icon: 'bi-file-earmark-zip' },
};

/**
 * Default tax rate (IVA in Costa Rica)
 */
export const DEFAULT_TAX_RATE = 13;

/**
 * Moneda por defecto de los documentos de cobro (#558).
 *
 * ProMedic es costarricense y cotiza en colones. Hasta #558 las facturas se
 * creaban con 'USD' hardcodeado en el carril MCP y, desde la GUI, con el
 * DEFAULT roto de la columna (el literal `'USD'` CON comillas simples adentro).
 * Los montos copiados de la cotizacion son colones, asi que rotularlos en
 * dolares erraba por ~500x en el documento que ve el cliente.
 *
 * Sigue siendo un DEFAULT, no un dato: `quotations` todavia no tiene columna de
 * moneda. La cura de fondo es esa columna, para que la factura la herede en vez
 * de asumirla.
 */
export const DEFAULT_CURRENCY = 'CRC';

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

/**
 * Date formats
 */
export const DATE_FORMAT = {
  DISPLAY: 'DD/MM/YYYY',
  INPUT: 'YYYY-MM-DD',
  DATETIME: 'DD/MM/YYYY HH:mm',
};

/**
 * API endpoints (for MCP server)
 */
export const API_ENDPOINTS = {
  BASE_URL: process.env.REACT_APP_MCP_SERVER_URL || 'http://localhost:3001',
  AGENT_CHAT: '/api/agent/chat',
  AGENT_TOOLS: '/api/agent/tools',
};

/**
 * Storage bucket names
 */
export const STORAGE_BUCKETS = {
  RECEIPTS: 'receipts',
  PROGRESS_PHOTOS: 'progress-photos',
  QUOTATION_FILES: 'quotation-files',
  INVENTORY_IMAGES: 'inventory-images',
  CHAT_FILES: 'chat-files',
  INVOICE_PDFS: 'invoice-pdfs',
};

/**
 * Query cache times (in milliseconds)
 */
export const CACHE_TIME = {
  SHORT: 1000 * 60 * 2, // 2 minutes
  MEDIUM: 1000 * 60 * 5, // 5 minutes
  LONG: 1000 * 60 * 10, // 10 minutes
};

/**
 * Common measurement units
 */
export const UNITS = [
  { value: 'unidad', label: 'Unidad' },
  { value: 'metro', label: 'Metro' },
  { value: 'metro cuadrado', label: 'Metro Cuadrado' },
  { value: 'metro cúbico', label: 'Metro Cúbico' },
  { value: 'kilogramo', label: 'Kilogramo' },
  { value: 'litro', label: 'Litro' },
  { value: 'hora', label: 'Hora' },
  { value: 'día', label: 'Día' },
  { value: 'caja', label: 'Caja' },
  { value: 'paquete', label: 'Paquete' },
  { value: 'rollo', label: 'Rollo' },
  { value: 'galón', label: 'Galón' },
  { value: 'pieza', label: 'Pieza' },
];

/**
 * Project number format pattern
 */
export const PROJECT_NUMBER_PATTERN = /^Q\d{3}EQ-SL-\d{4}$/;

/**
 * Validation messages
 */
export const VALIDATION_MESSAGES = {
  REQUIRED: 'Este campo es requerido',
  EMAIL_INVALID: 'Email inválido',
  NUMBER_INVALID: 'Debe ser un número válido',
  NUMBER_POSITIVE: 'Debe ser un número positivo',
  NUMBER_MIN: 'El valor es demasiado pequeño',
  NUMBER_MAX: 'El valor es demasiado grande',
  FILE_TOO_LARGE: 'El archivo es demasiado grande',
  FILE_TYPE_INVALID: 'Tipo de archivo no permitido',
};

/**
 * Material request statuses
 * Must match material_requests.status CHECK constraint in migration 027
 */
export const MATERIAL_REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CONSUMED: 'consumed',
  CANCELLED: 'cancelled',
};

/**
 * Material request status labels
 */
export const MATERIAL_REQUEST_STATUS_LABELS = {
  [MATERIAL_REQUEST_STATUS.PENDING]: 'Pendiente',
  [MATERIAL_REQUEST_STATUS.APPROVED]: 'Aprobada',
  [MATERIAL_REQUEST_STATUS.REJECTED]: 'Rechazada',
  [MATERIAL_REQUEST_STATUS.CONSUMED]: 'Consumida',
  [MATERIAL_REQUEST_STATUS.CANCELLED]: 'Cancelada',
};

/**
 * Material request priority (frontend-only, not in DB schema)
 * Can be stored in notes field or added via future migration
 */
export const MATERIAL_REQUEST_PRIORITY = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
};

/**
 * Time entry types
 */
export const TIME_ENTRY_TYPE = {
  CHECK_IN: 'check_in',
  CHECK_OUT: 'check_out',
  BREAK_START: 'break_start',
  BREAK_END: 'break_end',
};

/**
 * Time entry status (frontend-only, DB uses entry_type per row)
 * DB model: one row per event (check_in, check_out, break_start, break_end)
 * with auto-pairing trigger and calculated_hours
 */
export const TIME_ENTRY_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/**
 * Incident types
 * Must match incidents.incident_type CHECK constraint
 */
export const INCIDENT_TYPE = {
  SAFETY: 'safety',
  EQUIPMENT_FAILURE: 'equipment_failure',
  DELAY: 'delay',
  MATERIAL_ISSUE: 'material_issue',
  CLIENT_ISSUE: 'client_issue',
  WEATHER: 'weather',
  OTHER: 'other',
};

/**
 * Incident type labels
 */
export const INCIDENT_TYPE_LABELS = {
  [INCIDENT_TYPE.SAFETY]: 'Seguridad',
  [INCIDENT_TYPE.EQUIPMENT_FAILURE]: 'Falla de equipo',
  [INCIDENT_TYPE.DELAY]: 'Retraso',
  [INCIDENT_TYPE.MATERIAL_ISSUE]: 'Problema con materiales',
  [INCIDENT_TYPE.CLIENT_ISSUE]: 'Problema con cliente',
  [INCIDENT_TYPE.WEATHER]: 'Clima',
  [INCIDENT_TYPE.OTHER]: 'Otro',
};

/**
 * Incident severity levels
 */
export const INCIDENT_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

/**
 * Incident status
 * Must match incidents.status CHECK constraint
 */
export const INCIDENT_STATUS = {
  REPORTED: 'reported',
  ACKNOWLEDGED: 'acknowledged',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
};

/**
 * Signature types
 *
 * SOURCE OF TRUTH: the live CHECK constraint
 * `project_signatures_signature_type_check` on public.project_signatures:
 *
 *   CHECK (signature_type = ANY (ARRAY[
 *     'client_acceptance', 'project_closure',
 *     'incident_acknowledgment', 'invoice_receipt'
 *   ]))
 *
 * Do NOT trust migration 027_technician_portal.sql here: it declares a
 * different taxonomy (work_acceptance / delivery / material_receipt /
 * safety_acknowledgment) but was never applied to the live database — the
 * live table predates it and never carried those values (see #311, and #305
 * for the same divergence in the column list). Any value below that is not
 * in the constraint above fails the INSERT with SQLSTATE 23514.
 *
 * These four values are already what mcp-server/src/tools/signatureTools.js
 * and src/services/closureService.js emit, and the only value present in
 * live data.
 *
 * Adding a type here REQUIRES widening the CHECK constraint first.
 * src/services/__tests__/signatureService.test.js guards this invariant.
 */
export const SIGNATURE_TYPE = {
  CLIENT_ACCEPTANCE: 'client_acceptance',
  PROJECT_CLOSURE: 'project_closure',
  INCIDENT_ACKNOWLEDGMENT: 'incident_acknowledgment',
  INVOICE_RECEIPT: 'invoice_receipt',
};

/**
 * Signature type labels
 */
export const SIGNATURE_TYPE_LABELS = {
  [SIGNATURE_TYPE.CLIENT_ACCEPTANCE]: 'Aceptación del cliente',
  [SIGNATURE_TYPE.PROJECT_CLOSURE]: 'Cierre de proyecto',
  [SIGNATURE_TYPE.INCIDENT_ACKNOWLEDGMENT]: 'Reconocimiento de incidente',
  [SIGNATURE_TYPE.INVOICE_RECEIPT]: 'Recepción de factura',
};

/**
 * Quién puede CAPTURAR cada tipo de firma (#525).
 *
 * ESPEJO de mcp-server/src/utils/signaturePermissions.js. Los dos carriles no
 * pueden compartir el módulo (paquetes distintos), así que la duplicación la
 * vigila mcp-server/tests/tools/signatureLaneParity.test.js. La ley de verdad
 * es la RLS: supabase/migrations/20260729070000_techo_firmas.sql.
 *
 * La decisión de negocio está en wiki/pages/role-ui-matrix.md (sección
 * "Firmas"). El resumen: una firma tiene DOS personas — `signer_name` es quien
 * acepta, `signed_by` es quien operó el dispositivo. El técnico no FIRMA la
 * aceptación del cliente, la CAPTURA: le pasa la tablet al encargado de planta.
 * Por eso `client_acceptance` le queda permitida (acotada por RLS al proyecto
 * donde participa) y salen los dos tipos de oficina.
 */
export const TECHNICIAN_CAPTURABLE_SIGNATURE_TYPES = [
  SIGNATURE_TYPE.CLIENT_ACCEPTANCE,
  SIGNATURE_TYPE.INCIDENT_ACKNOWLEDGMENT,
];

/** `employee` es el nombre viejo del mismo rol — ver `is_technician()`. */
const TECHNICIAN_ROLES = ['technician', 'employee'];

export const isTechnicianRole = (role) => TECHNICIAN_ROLES.includes(role);

/**
 * ¿El ROL puede capturar ese TIPO? El ámbito por proyecto no se decide acá —
 * lo resuelve `has_project_stake()` dentro de la política.
 */
export const canCaptureSignatureType = (role, signatureType) => {
  if (!isTechnicianRole(role)) return true;
  return TECHNICIAN_CAPTURABLE_SIGNATURE_TYPES.includes(signatureType);
};

/** Mensaje que ve el técnico. Dice quién sí puede, para que no sea un callejón. */
export const signatureDenialMessage = (signatureType) => {
  if (signatureType === SIGNATURE_TYPE.PROJECT_CLOSURE) {
    return 'La firma de cierre de proyecto la registra administración desde la pantalla del proyecto. '
      + 'En obra se captura la aceptación del cliente.';
  }
  if (signatureType === SIGNATURE_TYPE.INVOICE_RECEIPT) {
    return 'La firma de recepción de factura la maneja administración.';
  }
  return `No tenés permiso para capturar firmas de tipo "${signatureType}".`;
};

/**
 * Checklist item status
 */
export const CHECKLIST_ITEM_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  NOT_APPLICABLE: 'not_applicable',
};

/**
 * Project message types
 */
export const PROJECT_MESSAGE_TYPE = {
  TEXT: 'text',
  SYSTEM: 'system',
  ALERT: 'alert',
  UPDATE: 'update',
};

const constants = {
  PROJECT_STATUS,
  INVOICEABLE_PROJECT_STATUSES,
  QUOTATION_STATUS,
  INVOICE_STATUS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANTS,
  INVOICE_PAYMENT_METHOD,
  INVOICE_PAYMENT_METHOD_LABELS,
  EXPENSE_CATEGORY,
  ITEM_TYPE,
  MOVEMENT_TYPE,
  INVENTORY_CATEGORY,
  INVENTORY_CATEGORY_LABELS,
  MOVEMENT_REASON,
  MOVEMENT_REASON_LABELS,
  USER_ROLE,
  TASK_STATUS,
  TASK_PRIORITY,
  TASK_CATEGORY,
  TASK_CATEGORY_LABELS,
  TASK_ASSIGNMENT_ROLE,
  TASK_MATERIAL_STATUS,
  BUDGET_THRESHOLD,
  FILE_UPLOAD,
  CHAT_ACCEPTED_TYPES,
  CHAT_FILE_LABELS,
  DEFAULT_TAX_RATE,
  DEFAULT_CURRENCY,
  PAGINATION,
  DATE_FORMAT,
  API_ENDPOINTS,
  STORAGE_BUCKETS,
  CACHE_TIME,
  UNITS,
  PROJECT_NUMBER_PATTERN,
  VALIDATION_MESSAGES,
  // Technician portal constants
  MATERIAL_REQUEST_STATUS,
  MATERIAL_REQUEST_STATUS_LABELS,
  MATERIAL_REQUEST_PRIORITY,
  TIME_ENTRY_TYPE,
  TIME_ENTRY_STATUS,
  INCIDENT_TYPE,
  INCIDENT_TYPE_LABELS,
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  SIGNATURE_TYPE,
  SIGNATURE_TYPE_LABELS,
  TECHNICIAN_CAPTURABLE_SIGNATURE_TYPES,
  isTechnicianRole,
  canCaptureSignatureType,
  signatureDenialMessage,
  CHECKLIST_ITEM_STATUS,
  PROJECT_MESSAGE_TYPE,
};

export default constants;
